import {
	CloseCircleFilled,
	EyeOutlined,
	FormOutlined,
	LoadingOutlined,
	PlusOutlined,
	UploadOutlined,
} from '@ant-design/icons';
import { useMutation, useQuery } from '@apollo/client';
import {
	Button,
	Col,
	Modal,
	Row,
	Space,
	Spin,
	Form,
	message,
	Input,
	Upload,
} from 'antd';
import type { UploadFile } from 'antd/es/upload/interface';
import Text from 'antd/lib/typography/Text';
import React, { useCallback, useState } from 'react';
import { CustomNotification } from 'src/components/custom-notification';
import { tableColumnTextFilterConfig } from 'src/components/tableUtils';
import { uploadAuthorPicture } from 'src/lib/axios';
import {
	MutationCreateAuthor,
	MutationDeleteAuthor,
	MutationUpdateAuthor,
} from 'src/lib/gql/mutations';
import { QueryGetAuthors } from 'src/lib/gql/queries';
import InfoTable from '../components/info-table';
import {
	CmsCreateAuthorMutation,
	CmsCreateAuthorMutationVariables,
	CmsDeleteAuthorMutation,
	CmsDeleteAuthorMutationVariables,
	CmsGetAuthorsQuery,
	CmsGetAuthorsQueryVariables,
	CmsUpdateAuthorMutation,
	CmsUpdateAuthorMutationVariables,
} from 'src/lib/generated/_generated';

interface AuthorTable {
	key: string;
	id: string;
	name: string;
	jobTitle: string;
	bio: string;
	pictureUrl: string;
}

const Authors = () => {
	const [isModalVisible, setIsModalVisible] = useState(false);
	const [isViewModalVisible, setIsViewModalVisible] = useState(false);
	const [editingAuthor, setEditingAuthor] = useState<any>(null);
	const [viewingAuthor, setViewingAuthor] = useState<any>(null);
	const [fileList, setFileList] = useState<UploadFile[]>([]);
	const [uploading, setUploading] = useState(false);
	const [form] = Form.useForm();

	const {
		data: qryData,
		loading,
		refetch: refetchQuery,
	} = useQuery<CmsGetAuthorsQuery, CmsGetAuthorsQueryVariables>(QueryGetAuthors, {
		fetchPolicy: 'cache-and-network',
		variables: {
			searchBy: null,
			orderBy: null,
		},
	});

	const [createAuthor, { loading: creating }] = useMutation<
		CmsCreateAuthorMutation,
		CmsCreateAuthorMutationVariables
	>(MutationCreateAuthor, {
		onError(error) {
			CustomNotification({
				pageName: 'Authors',
				pagePrefix: 'Author',
				notificationType: 'custom-error',
				customDescription: error.message,
			});
		},
	});

	const [updateAuthor, { loading: updating }] = useMutation<
		CmsUpdateAuthorMutation,
		CmsUpdateAuthorMutationVariables
	>(MutationUpdateAuthor, {
		onError(error) {
			CustomNotification({
				pageName: 'Authors',
				pagePrefix: 'Author',
				notificationType: 'custom-error',
				customDescription: error.message,
			});
		},
	});

	const [DeleteAuthor] = useMutation<
		CmsDeleteAuthorMutation,
		CmsDeleteAuthorMutationVariables
	>(MutationDeleteAuthor, {
		onError(error) {
			CustomNotification({
				pageName: 'Authors',
				pagePrefix: 'Author',
				notificationType: 'custom-error',
				customDescription: error.message,
			});
		},
	});

	const p_Authors: AuthorTable[] = [];

	qryData?.getAuthors?.forEach((author, i) => {
		return p_Authors.push({
			key: (i + 1).toString(),
			id: author?.id || '',
			name: `${author?.fName || ''} ${author?.lName || ''}`.trim(),
			jobTitle: author?.jobTitle || '',
			bio: author?.bio || '',
			pictureUrl: author?.picture?.url || '',
		});
	});

	const handleCreate = () => {
		setEditingAuthor(null);
		form.resetFields();
		setFileList([]);
		setIsModalVisible(true);
	};

	const handleView = (record: AuthorTable) => {
		const author = qryData?.getAuthors?.find((a) => a?.id === record.id);
		setViewingAuthor(author);
		setIsViewModalVisible(true);
	};

	const handleEdit = (record: AuthorTable) => {
		const author = qryData?.getAuthors?.find((a) => a?.id === record.id);
		setEditingAuthor(author);
		form.setFieldsValue({
			fName: author?.fName || '',
			lName: author?.lName || '',
			jobTitle: author?.jobTitle || '',
			bio: author?.bio || '',
		});
		setFileList([]);
		setIsModalVisible(true);
	};

	const deleteAuthor = useCallback(
		async (authorId: string) => {
			if (!confirm('Are you sure you want to delete this author?')) return;

			const rsp = await DeleteAuthor({
				variables: {
					authorId,
				},
			});

			if (rsp.data) {
				CustomNotification({
					pageName: 'Authors',
					pagePrefix: 'Author',
					notificationType: 'deleted',
				});
				refetchQuery();
			}
		},
		[DeleteAuthor, refetchQuery],
	);

	const handleSubmit = async (values: any) => {
		try {
			let authorId = editingAuthor?.id;

			if (editingAuthor) {
				// Update existing author
				const rsp = await updateAuthor({
					variables: {
						authorId: editingAuthor.id,
						data: {
							fName: values.fName,
							lName: values.lName,
							jobTitle: values.jobTitle,
							bio: values.bio,
						},
					},
				});

				if (rsp.data) {
					authorId = rsp.data.updateAuthor?.id;
				}
			} else {
				// Create new author
				const rsp = await createAuthor({
					variables: {
						data: {
							fName: values.fName,
							lName: values.lName,
							jobTitle: values.jobTitle,
							bio: values.bio,
						},
					},
				});

				if (rsp.data) {
					authorId = rsp.data.createAuthor?.id;
				}
			}

			// Upload picture if file is selected
			if (fileList.length > 0 && fileList[0].originFileObj && authorId) {
				setUploading(true);
				try {
					await uploadAuthorPicture({
						authorId: authorId,
						file: fileList[0].originFileObj as File,
					});
					message.success('Author picture uploaded successfully!');
				} catch (error: any) {
					message.error(error?.response?.data?.message || 'Failed to upload picture');
				} finally {
					setUploading(false);
				}
			}

			CustomNotification({
				pageName: 'Authors',
				pagePrefix: 'Author',
				notificationType: editingAuthor ? 'updated' : 'created',
			});
			setIsModalVisible(false);
			setFileList([]);
			refetchQuery();
		} catch (error) {
			message.error(`Failed to ${editingAuthor ? 'update' : 'create'} author`);
		}
	};

	// Fields
	const columns = [
		{
			title: 'ID',
			dataIndex: 'key',
			width: 50,
			align: 'center' as const,
			render: (key: number) => {
				return key;
			},
		},
		{
			title: 'Name',
			dataIndex: 'name',
			key: 'name',
			width: 200,
			...tableColumnTextFilterConfig<AuthorTable>(),
			onFilter: (value: any, record: AuthorTable) => {
				return record.name
					.toString()
					.toLowerCase()
					.includes(value.toString().toLowerCase());
			},
		},
		{
			title: 'Job Title',
			dataIndex: 'jobTitle',
			key: 'jobTitle',
			width: 200,
		},
		{
			title: 'Bio',
			dataIndex: 'bio',
			key: 'bio',
			width: 300,
			render: (bio: string) => (
				<div
					style={{
						maxWidth: '300px',
						overflow: 'hidden',
						textOverflow: 'ellipsis',
						whiteSpace: 'nowrap',
					}}
				>
					{bio}
				</div>
			),
		},
		{
			title: 'Picture',
			dataIndex: 'pictureUrl',
			key: 'pictureUrl',
			width: 100,
			align: 'center' as const,
			render: (url: string) =>
					<img
						src={url? url : '/avatar-img.png' }
						alt="Author"
						style={{ width: 40, height: 40, borderRadius: '50%', objectFit: 'cover' }}
					/>
		},
		{
			title: 'Edit',
			key: 'edit',
			width: 80,
			align: 'center' as const,
			render: (record: AuthorTable) => {
				return (
					<Space>
						<EyeOutlined
							onClick={() => handleView(record)}
							style={{
								color: 'black',
								width: '12px',
								height: '12px',
								cursor: 'pointer',
							}}
							title="View"
						/>
						<FormOutlined
							onClick={() => handleEdit(record)}
							style={{
								color: 'black',
								width: '12px',
								height: '12px',
								cursor: 'pointer',
							}}
							title="Edit"
						/>
						<CloseCircleFilled
							onClick={() => deleteAuthor(record.id)}
							style={{
								color: 'var(--invalid)',
								width: '12px',
								height: '12px',
								cursor: 'pointer',
							}}
							title="Remove"
						/>
					</Space>
				);
			},
		},
	];

	return (
		<Row>
			<Col span={24}>
				<Text className="sub-header">Authors</Text>
			</Col>

			<Col span={24}>
				<Row
					gutter={16}
					style={{
						margin: '16px 0',
					}}
					align={'bottom'}
				>
					<Button className="btn-main" onClick={handleCreate}>
						Create New Author
						<PlusOutlined
							style={{
								position: 'relative',
								top: '1.5px',
							}}
						/>
					</Button>
				</Row>

				<Spin indicator={<LoadingOutlined />} spinning={loading}>
					<InfoTable
						data={p_Authors}
						columnData={columns}
						onChange={() => {
							//
						}}
						other={{
							scroll: {
								x: 900,
							},
						}}
					/>
				</Spin>
			</Col>

			<Modal
				title="View Author"
				open={isViewModalVisible}
				onCancel={() => setIsViewModalVisible(false)}
				footer={[
					<Button key="close" onClick={() => setIsViewModalVisible(false)}>
						Close
					</Button>,
				]}
				width={600}
			>
				{viewingAuthor && (
					<div style={{ marginTop: '20px' }}>
						<div style={{ textAlign: 'center', marginBottom: '20px' }}>
							<img
								src={
									viewingAuthor.picture?.url
										? viewingAuthor.picture.url
										: '/avatar-img.png'
								}
								alt="Author"
								style={{
									width: 120,
									height: 120,
									borderRadius: '50%',
									objectFit: 'cover',
								}}
							/>
						</div>
						<div style={{ marginBottom: '16px' }}>
							<Text strong>First Name: </Text>
							<Text>{viewingAuthor.fName || 'N/A'}</Text>
						</div>
						<div style={{ marginBottom: '16px' }}>
							<Text strong>Last Name: </Text>
							<Text>{viewingAuthor.lName || 'N/A'}</Text>
						</div>
						<div style={{ marginBottom: '16px' }}>
							<Text strong>Job Title: </Text>
							<Text>{viewingAuthor.jobTitle || 'N/A'}</Text>
						</div>
						<div style={{ marginBottom: '16px' }}>
							<Text strong>Bio: </Text>
							<Text>{viewingAuthor.bio || 'N/A'}</Text>
						</div>
					</div>
				)}
			</Modal>

			<Modal
				title={editingAuthor ? 'Edit Author' : 'Create Author'}
				open={isModalVisible}
				onCancel={() => setIsModalVisible(false)}
				footer={null}
				width={600}
			>
				<Form
					form={form}
					layout="vertical"
					onFinish={handleSubmit}
					style={{ marginTop: '20px' }}
				>
					<Form.Item
						name="fName"
						label="First Name"
						rules={[{ required: true, message: 'Please input first name!' }]}
					>
						<Input />
					</Form.Item>

					<Form.Item
						name="lName"
						label="Last Name"
						rules={[{ required: true, message: 'Please input last name!' }]}
					>
						<Input />
					</Form.Item>

					<Form.Item
						name="jobTitle"
						label="Job Title"
						rules={[{ required: true, message: 'Please input job title!' }]}
					>
						<Input />
					</Form.Item>

					<Form.Item name="bio" label="Bio">
						<Input.TextArea rows={4} />
					</Form.Item>

					<Form.Item label="Author Picture">
							<div style={{ marginBottom: '16px' }}>
								<img
									src={
										editingAuthor?.picture?.url
											? viewingAuthor.picture.url
											: '/avatar-img.png'
									}
									alt="Current"
									style={{
										width: 100,
										height: 100,
										borderRadius: '50%',
										objectFit: 'cover',
									}}
								/>
								<div style={{ marginTop: '8px' }}>
									<Text type="secondary">Current picture</Text>
								</div>
							</div>
						<Upload
							listType="picture"
							fileList={fileList}
							beforeUpload={(file) => {
								const isImage = file.type.startsWith('image/');
								if (!isImage) {
									message.error('You can only upload image files!');
									return false;
								}
								const isLt1M = file.size / 1024 / 1024 < 1;
								if (!isLt1M) {
									message.error('Image must be smaller than 1MB!');
									return false;
								}
								const uploadFile: UploadFile = {
									uid: file.uid || Date.now().toString(),
									name: file.name,
									status: 'done',
									originFileObj: file as any,
								};
								setFileList([uploadFile]);
								return false;
							}}
							onRemove={() => {
								setFileList([]);
							}}
							maxCount={1}
						>
							<Button icon={<UploadOutlined />}>Select Picture</Button>
						</Upload>
					</Form.Item>

					<Form.Item>
						<Space style={{ width: '100%', justifyContent: 'flex-end' }}>
							<Button onClick={() => setIsModalVisible(false)}>Cancel</Button>
							<Button
								type="primary"
								htmlType="submit"
								loading={creating || updating || uploading}
							>
								{editingAuthor ? 'Update' : 'Create'}
							</Button>
						</Space>
					</Form.Item>
				</Form>
			</Modal>
		</Row>
	);
};

export default Authors;
